import { Show, createSignal, onCleanup } from 'solid-js';
import { Portal } from 'solid-js/web';
import type { JSX } from 'solid-js';
import type { ObjectContext } from '../../project/objects';
import type { SelectionContext } from '../selection';
import type { DragContext } from '../drag';
import type { HistoryContext } from 'Shared/posters/project/history';
import { Mode } from 'Shared/posters/project/PosterMode';
import { moveObjectCmd, moveMultipleObjectsCmd, reorderChildCmd, type MultiMoveEntry } from 'Shared/posters/project/commands';
import { setObjectPositionSize, setDetachedInStore, moveChildToIndexInStore } from 'Shared/posters/project/objects';
import type { RuntimeContainer, Position, Box } from 'Shared/posters/types';
import { onDocumentEvent } from '../utils';
import { cycleSelectionAt } from 'Shared/posters/canvas/interactions/cycleSelection';
import { ResizeHandles } from 'Shared/posters/canvas/interactions/ResizeHandles';
import { ResizeMode } from 'Shared/posters/canvas/interactions/ResizeMode';
import { InterceptTimer } from 'Shared/posters/canvas/interactions/InterceptTimer';
import { clampDragPos } from './dragBounds';
import { detachIfLinked, detachmentCommit, linkedBox, lookupLeaf } from 'Shared/posters/canvas/interactions/linkedLeaf';
import { type DraggableImpl } from 'Shared/posters/canvas/interactions/ObjectImpl';
import { createMemo } from 'solid-js';
import { useUser } from 'Shared/SiteProvider';
import { Canvas } from 'Shared/posters/project/posterSchema';
import type { Dimensions, ImplRegistry } from 'Shared/posters/canvas/interactions/ObjectImpl';
import type { DesignLinkContext } from 'Shared/posters/project/designLink';

interface ISelectableObject<O extends DraggableImpl>
{
	// The object's host configuration + behaviour (flags, resize handles, the resize/fit
	// callbacks, the ObjectBehaviour seam, the registry accessors). Each object type's *Actions
	// subclass supplies it; the host reads everything type-specific from here.
	impl: O;

	objects: ObjectContext;
	selection: SelectionContext;
	history: HistoryContext;
	drag: DragContext;
	canvas: Canvas;
	// User-mode link to the nested template design; drives a linked leaf's position/size and detach.
	design: DesignLinkContext;

	children: (dims: () => Dimensions) => JSX.Element

	parentOffset?: () => Position
	parentId?:     string | null

	mode: Mode;
}


// Close out a text resize on mouseup — the far end of the gesture TextResizeHandles opened, which is
// why the mode and any detachment it took travel on the shared ResizeMode. The impl picks its own
// command to commit with (corner, width or field reserve), so the detachment is folded in by handing
// it a decorated history context rather than by threading a slot through each one. An impl whose
// resize end commits nothing leaves that detachment on no history entry at all, so it is given back.
function endTextResize(impl:DraggableImpl,resizeMode:ResizeMode,objCtx:ObjectContext,impls:ImplRegistry,histCtx:HistoryContext)
{
	const mode = resizeMode.mode();
	const slot = resizeMode.detachedSlot();
	resizeMode.end();
	if (!slot) {
		impl.onResizeEnd(mode, objCtx, impls, histCtx);
		return;
	}
	const commit = detachmentCommit(histCtx, objCtx, impl.id, slot);
	impl.onResizeEnd(mode, objCtx, impls, commit.history);
	if (!commit.committed())
		setDetachedInStore(objCtx, impl.id, null);
}


// ── DraggableElement ──────────────────────────────────────────────────────────

export function SelectableObject<O extends DraggableImpl>(P: ISelectableObject<O>)
{
//TODO avoid using SelectableObject when in read only mode
	const isSelected = () => P.selection?.selectedIds().has(P.impl.id) ?? false;
	const isEditing = () => P.selection ? P.selection?.editing() == P.impl.id : false;
	const isPinned = () => P.selection?.pinned() == P.impl.id;

//XXX cf extended enumerations/classes
//	let resizeMode = '';  // '' | 'textCorner' | 'textWidth' | 'fieldWidth'
	let resizeMode = new ResizeMode();

	// ── Same-element double-click detection ───────────────────────────────────────
	// When the element is NOT yet selected, the first click selects it and causes
	// SolidJS to mount resize handles into handlesObject (which is always on top).
	// Those handles can intercept the second click, putting the two clicks in
	// different DOM subtrees so the browser never fires dblclick on the element's
	// own <g>.  We detect the double-click manually via timing in onMouseDown.
	const firstSelect = {time:0, x:0, y:0};

	// ── Intercept-rect readiness ───────────────────────────────────────────────
	// The intercept rect must NOT appear immediately on selection.  A fast
	// double-click (< ~300 ms) would cause the first mousedown to land on this
	// element's own <g> and the second on the intercept rect in handlesObject —
	// two different DOM subtrees.  Browsers fire dblclick on their lowest common
	// ancestor (the SVG root), which has no handler, so text editing never starts.
	// Delaying by 300 ms means both clicks of a fast double-click land on the
	// element's own <g>, letting dblclick fire there where the impl's handleDoubleClick runs.
	//
	// Armed from onMouseDown (first-click selection). The JSX Show gate enforces isOnlySelected() so the
	// rect stays hidden in a multi-selection, and !isEditing() so it stays hidden while the text editor
	// (rendered by TextObject) is open — reappearing when editing ends without needing a re-arm.
	const [interceptReady, setInterceptReady] = createSignal(false);

	const interceptTimer = new InterceptTimer(setInterceptReady);

	// The live geometry (pos/effectiveBox/contentOffsetY) lives on the impl now — its store is built
	// and injected by the create*Impl factory — so the host reads it through P.impl rather than owning it.

	// Runs synchronously during setup, not on mount: the registry stores the impl, the cleanup/document
	// listener bind to the setup owner, and setupGeometry (text's initial fit, re-run on font load) binds
	// its onGeometryReady cleanup to this owner. Inert for non-text impls.
	P.impl.setupGeometry();

	/* Copy these for later use in onCleanup() */
	const implRef = P.impl;
	const implId  = implRef.id;

	P.selection.registerImpl(implId, implRef);

	onCleanup(() => {
		interceptTimer.disarm();
		P.selection.unregisterImpl(implId, implRef);
	})

	onDocumentEvent('mouseup', () => endTextResize(P.impl, resizeMode, P.objects, P.selection.impls, P.history))

	// ── Cursor ─────────────────────────────────────────────────────────────────
//XXX cf class or something...	
	const cursor = () => {
		if (isEditing())  return 'text';
		if (isSelected()) return 'move';
		return 'pointer';
	}

	// When a selected element is visually obscured by another element on top, the
	// topmost SVG element normally claims the cursor and all mouse events.  We fix
	// this by rendering a transparent rect in handlesObject (the last <g> in the
	// SVG) that covers exactly the selected element's bbox.  Because handlesObject
	// is always on top, the rect receives pointer events first, shows the move
	// cursor, and handles dragging — even when another object is painted over the
	// selected one.
	//

	const box = createMemo(() => linkedBox(P.mode, P.design, P.impl, !useUser()));

	// Alt is the selection-cycling modifier, so an Alt-double-click must never open the editor or the
	// image dialog — the two clicks of one cycle land on different elements, but a stray dblclick that
	// did reach here would otherwise act on it.
	const doDoubleClick = e => { if (!e.altKey) P.impl.handleDoubleClick(e,P.selection,P.mode) };
	const mouseDown = e => onMouseDown(e,P.parentId,P.impl,P.canvas,P.selection,P.selection.impls,P.drag,P.objects,P.history,P.design,firstSelect,interceptTimer,P.mode);

	const cursorStyle = { cursor: P.impl.cursorOverride(P.selection) ?? cursor() };

	// The size the object presents. In user mode a linked (non-detached) leaf shows the size the
	// template laid it out at — lookupLeaf, resolving the commented stub — so it stays consistent with
	// the reflowed layout; a detached/unlinked object measures its own box.
	const selDims = () => lookupLeaf(P.mode, P.design, P.impl);

	return (
		<>
			<g
				transform={`translate(${box().pos.x}, ${box().pos.y})`}
				id={P.impl.id}
				style={cursorStyle}
				onMouseDown={mouseDown}
				onDblClick={doDoubleClick}
			>
				{/* The whole-box hit target: this is what makes an object selectable (and Alt-click-cyclable
					via objectsAtPoint) anywhere in its box rather than only on its ink. Offset by
					contentOffsetY for the same reason getHitBBox/the intercept rect/the resize handles are:
					text renders its glyphs above the box origin, so a rect at y=0 sits a line-ascent *below*
					the glyphs — leaving the whitespace either side of the text unhittable. */}
				<Show when={selDims().width && selDims().height}>
					<rect x="0" y={P.impl.contentOffsetY()} width={selDims().width} height={selDims().height} fill="transparent" />
				</Show>

				{P.children(selDims)}
			</g>

			<Show when={isSelected() && P.drag.handlesObject()}>
				<Portal mount={P.drag.handlesObject()!} isSVG>
					{/* A pinned object needs its intercept rect at once, not on the double-click delay: the
						click that follows an Alt-click cycle has to reach the object the cycle landed on
						rather than whatever is painted over it. The rect carries its own onDblClick, so
						editing still opens. */}
					<Selection {...P} impls={P.selection.impls} interceptReady={interceptReady() || isPinned()} resizeMode={resizeMode} />
				</Portal>
			</Show>
		</>
	);
}

function Selection<O extends DraggableImpl>(P: ISelectableObject<O>)
{
	const isOnlySelected = () => P.selection.selected() == P.impl.id && P.selection.selectedIds().size == 1;
	const box = createMemo(() => linkedBox(P.mode, P.design, P.impl, !useUser()));

	const parentOffset = createMemo(() => P.parentOffset?.() ?? {x:0,y:0});
	const absLeft = () => parentOffset().x + box().pos.x;
	const absTop  = () => parentOffset().y + box().pos.y;

	return (<>
		<g transform={`translate(${absLeft()}, ${absTop() + P.impl.contentOffsetY()})`} pointer-events="none">
			{P.impl.selectionComponent()}
		</g>
		<Show when={isOnlySelected()}>
			<InterceptionRectangle {...P} />

			<Show when={P.impl.resizeHandles() != 'none'}>
				<ResizeHandles
					objects={P.objects}
					selection={P.selection}
					history={P.history}
					drag={P.drag}
					impl={P.impl}
					canvas={P.canvas}
					design={P.design}
					mode={P.mode}
					resizeMode={P.resizeMode}
					parentOffset={P.parentOffset}
				/>
			</Show>
		</Show>
	</>);
}

/* 
	This rectangle sits on top of all canvas content (in handlesObject) so the selected 
	element keeps the move cursor and can be dragged even when another object is painted over it.  
	Rendered before the resize handles so those remain clickable on top of it. Delayed 300 ms after 
	selection so fast double-clicks still land on the element's own <g> and fire dblclick there 
	(see interceptReady). 
*/
function InterceptionRectangle(P)
{
	const isEditing = () => P.selection ? P.selection?.editing() == P.impl.id : false;

	const box = createMemo(() => linkedBox(P.mode, P.design, P.impl, !useUser()));
//XXX can parentOffset change? Perhaps we don't need the creatememo() here?	
	const parentOffset = createMemo(() => P.parentOffset?.() ?? {x:0,y:0});

	const selW = () => P.impl.getSize().width;
	const selH = () => P.impl.getSize().height;

	const absLeft = () => parentOffset().x + box().pos.x;
	const absTop  = () => parentOffset().y + box().pos.y;

	const interceptMouseDown = e => onInterceptMouseDown(e,P.parentId,P.impl,P.canvas,P.selection,P.impls,P.drag,P.objects,P.history,P.design,P.mode);
	const doDoubleClick = e => { if (!e.altKey) P.impl.handleDoubleClick(e,P.selection,P.mode) };

	return (
		<Show when={P.interceptReady && !isEditing()}>
			<rect
				data-keeps-selection=''
				x={absLeft()} y={absTop() + P.impl.contentOffsetY()}
				width={selW()} height={selH()}
				fill="transparent"
				style={{ cursor: 'move' }}
				onMouseDown={interceptMouseDown}
				onDblClick={doDoubleClick}
			/>
		</Show>
	);
}

const SAME_DBLCLICK_MS = 500;

// ── Vertical reorder (flex-container children) ───────────────────────────────
// A flex-laid-out child can't be freely positioned, but it can be dragged up/down to
// reorder it within its container. The dragged child follows the cursor (via the
// reorderDrag signal the container reads), while the array order — which drives the
// laid-out order — is updated live so siblings reflow. The net move is committed once
// on release. toIndex = count of siblings whose live centre sits above the cursor.
function startReorderDrag<O extends DraggableImpl>(e: MouseEvent,parentId:string|null,canvas:Canvas,impl:O,impls:ImplRegistry,dragCtx:DragContext,objCtx:ObjectContext,histCtx:HistoryContext)
{
	if (!parentId) 
		return;
	const parent = objCtx.getObject(parentId) as RuntimeContainer | null;
	const fromIndex = (parent?.objects ?? []).findIndex(o => o.id === impl.id);
	if (fromIndex < 0) 
		return;

	const startBBox = impls[impl.id]?.getBBox?.();
	const startTop = startBBox?.pos.y ?? 0;
	const h = startBBox?.dims.height ?? (impl.box.dims.height ?? 0);
	const ch = canvas.height ?? Infinity;

	dragCtx.beginDrag({ x: e.clientX, y: e.clientY },
		(_dx, dy) => {
			const floatTop = Math.max(0, Math.min(startTop + dy, ch - h));
			dragCtx.setReorderDrag({ id: impl.id, top: floatTop });
			const centre = floatTop + h / 2;
			const siblings = (objCtx.getObject(parentId) as RuntimeContainer | null)?.objects ?? [];
			let target = 0;
			for (const sib of siblings) {
				if (sib.id === impl.id) 
					continue;
				const b = impls[sib.id]?.getBBox?.();
				if (b && b.pos.y + b.dims.height / 2 < centre) 
					target++;
			}
			const cur = siblings.findIndex(o => o.id === impl.id);
			if (cur !== target) 
				moveChildToIndexInStore(objCtx, parentId, impl.id, target);
		},
		() => {
			dragCtx.setReorderDrag(null);
			const siblings = (objCtx.getObject(parentId) as RuntimeContainer | null)?.objects ?? [];
			const toIndex  = siblings.findIndex(o => o.id === impl.id);
			if (toIndex >= 0 && toIndex !== fromIndex)
				histCtx.execute(reorderChildCmd(objCtx, parentId, impl.id, fromIndex, toIndex));
		},
	)
}

function onMouseDown<O extends DraggableImpl>(e: MouseEvent,parentId:string|null,impl:O,canvas:Canvas,selCtx:SelectionContext,impls:ImplRegistry,dragCtx:DragContext,objCtx:ObjectContext,histCtx:HistoryContext,design:DesignLinkContext,firstSelect,interceptTimer,mode:Mode)
{
	if (selCtx.editing() === impl.id)
		return;

	e.preventDefault();
	e.stopPropagation();

	// Ctrl/Cmd-click: toggle multi-selection without starting a drag
	if (e.ctrlKey || e.metaKey) {
		selCtx.toggleSelect(impl.id);
		return;
	}

	// Alt-click: cycle the selection through everything under the cursor (see cycleSelectionAt).
	// Ahead of the parent rule below — cycling reaches a child directly.
	if (e.altKey) {
		cycleSelectionAt({ x: e.clientX, y: e.clientY }, dragCtx.svgRef(), selCtx, impls);
		return;
	}

	// A click on a container's child selects the container. Pinned, because the container gets no
	// mousedown of its own here (the click landed on the child) and so has no timer to arm: without the
	// rect the next click falls through this rule and selects the child, and a container its children
	// cover completely — same position, same size — can never be dragged at all.
	if (parentId && !selCtx.selectedIds().has(impl.id) && !selCtx.selectedIds().has(parentId)) {
		selCtx.selectPinned(parentId);
		return;
	}

	if (! selCtx.selectedIds().has(impl.id)) {
		// Record this click so that a quick second click can be detected as a
		// double-click even if resize handles have since appeared on top.
		firstSelect.time = Date.now();
		firstSelect.x = e.clientX;
		firstSelect.y = e.clientY;
		selCtx.select(impl.id);
		// Every selection arms it, top-level objects included: the rect is what keeps a plain click
		// anywhere over the selected object dragging *it* rather than selecting whatever is painted on
		// top of it. The delay still protects the double-click (see interceptReady).
		interceptTimer.arm();
	} 
	// Second click on the already-selected element: a same-spot quick re-click reads as a
	// double-click (open the text editor, or run the image's upload/rebind). Shift is excluded
	// so shift+click stays available for image crop-pan.
	else if (impl.handlesDoubleClick() && !e.shiftKey && detectSameElementDoubleClick(e,impl,selCtx,firstSelect)) {
		impl.handleDoubleClick(e,selCtx,mode);
		return;
	}

	// Shift-drag: an image's crop-pan when supplied, otherwise reserved (no canvas move).
	if (e.shiftKey) {
		impl.onShiftDrag(e, dragCtx, histCtx);
		return;
	}

	// Non-movable elements (a full-bleed image) select but never drag.
	if (!impl.isMovable())
		return;

	// A flex-laid-out child's position is owned by its container, so a free move makes no
	// sense — instead a vertical drag reorders it among its siblings.
	if (impl.isLayoutManaged()) {
		startReorderDrag(e,parentId,canvas.height,impl,impls,dragCtx,objCtx,histCtx);
		return;
	}

	startDrag(e,impl,parentId,canvas,selCtx,impls,dragCtx,objCtx,histCtx,design,mode);
}

function startDrag<O extends DraggableImpl>(e: MouseEvent,impl:O,parentId:string|null,canvas:Canvas,selCtx:SelectionContext,impls:ImplRegistry,dragCtx:DragContext,objCtx:ObjectContext,histCtx:HistoryContext,design:DesignLinkContext,mode:Mode)
{
	/* Multi-selection drag: */
	const dragIds = [...selCtx.selectedIds()];
	if (dragIds.length > 1) {
		beginMultiDrag(e,parentId,canvas,dragIds,impls,dragCtx,objCtx,histCtx,design,mode);
		return;
	}

	/* Single drag: */
	// A linked leaf detaches here; the returned origin is its current laid-out position so the drag
	// continues from where it sat rather than from its stale stored box.
	const detached = detachIfLinked(impl,design,objCtx,mode);

	// Size for the canvas-edge clamp comes from getSize() (the live hugged box for an un-resized
	// user-mode field, pos otherwise) so a record-widened field can't be dragged off the edge.
	const startBox: Box = { pos: detached?.pos ?? { x: impl.box.pos.x, y: impl.box.pos.y }, dims: impl.getSize() };

	dragCtx.beginDrag({ x: e.clientX, y: e.clientY },
		(dx, dy) => {
			const parentBBox = parentId ? impls[parentId]?.getBBox?.() : null
			const bounds: Dimensions = {
				width:  parentBBox?.dims.width  ?? (canvas.width  ?? Infinity),
				height: parentBBox?.dims.height ?? (canvas.height ?? Infinity),
			}
			impl.writePos(clampDragPos(startBox, dx, dy, bounds, impl.contentOffsetY()))
		},
		() => commitSingleMove(impl,startBox.pos,detached?.slot ?? null,objCtx,histCtx),
	)
}

// A same-spot quick re-click on the already-selected element, detected via either the local
// first-select time or the context's selectGesture (set when another element's intercept rect
// redirected selection here, so firstSelect was never set locally). Returns true and clears the
// gesture when it fires; the caller then runs the double-click action.
function detectSameElementDoubleClick<O extends DraggableImpl>(e: MouseEvent,impl:O,selCtx:SelectionContext,firstSelect): boolean
{
	const gesture = selCtx.selectGesture();
	const useGesture = !firstSelect.time && gesture?.id === impl.id;
	const firstTime = firstSelect.time || (useGesture ? gesture!.time : 0);
	const firstX = firstSelect.time ? firstSelect.x : (useGesture ? gesture!.pos.x : 0);
	const firstY = firstSelect.time ? firstSelect.y : (useGesture ? gesture!.pos.y : 0);
	if (
		Date.now() - firstTime < SAME_DBLCLICK_MS &&
		Math.abs(e.clientX - firstX) < 5 &&
		Math.abs(e.clientY - firstY) < 5
	) {
		firstSelect.time = 0;
		selCtx.clearSelectGesture();
		return true;
	}
	return false;
}

// Drag every selected object as one unit: capture each start bbox, then on each frame find the
// tightest clamped delta that keeps them all in bounds and apply it to all, committing once on
// release. getHitBBox is used for the boundary math so text (whose content can start above pos.top)
// is constrained correctly at the poster edge.
//
// Every linked leaf in the selection detaches first, exactly as a single drag does — and before the
// start bboxes are read, since detaching is what writes the laid-out position into the live box the
// bbox comes from. Without it the whole selection was dragged off a stale box and then snapped back
// by the next layout pass.
function beginMultiDrag(e: MouseEvent,parentId:string|null,canvas:Canvas,dragIds: string[],impls:ImplRegistry,dragCtx:DragContext,objCtx:ObjectContext,histCtx:HistoryContext,design:DesignLinkContext,mode:Mode)
{
	const slots: Record<string, Dimensions> = {}
	for (const id of dragIds) {
		const impl = impls[id]
		if (!impl) continue
		const detached = detachIfLinked(impl, design, objCtx, mode)
		if (detached) slots[id] = detached.slot
	}

	const startBboxes:    Record<string, Box> = {}
	const startHitBboxes: Record<string, Box> = {}
	for (const id of dragIds) {
		const bbox = impls[id]?.getBBox?.()
		if (bbox) startBboxes[id] = bbox
		const hitBbox = impls[id]?.getHitBBox?.()
		if (hitBbox) startHitBboxes[id] = hitBbox
	}

	dragCtx.beginDrag({ x: e.clientX, y: e.clientY },
		(dx, dy) => {
			const parentBBox = parentId ? impls[parentId]?.getBBox?.() : null
			const bounds: Dimensions = {
				width:  parentBBox?.dims.width  ?? (canvas.width  ?? Infinity),
				height: parentBBox?.dims.height ?? (canvas.height ?? Infinity),
			}
			let minDx = -Infinity, maxDx = Infinity
			let minDy = -Infinity, maxDy = Infinity
			for (const id of dragIds) {
				const sb  = startBboxes[id]
				const shb = startHitBboxes[id] ?? sb   // hit bbox for boundary math
				if (!sb || !shb)
					continue;
				minDx = Math.max(minDx, -shb.pos.x)
				maxDx = Math.min(maxDx, bounds.width - shb.dims.width - shb.pos.x)
				minDy = Math.max(minDy, -shb.pos.y)
				maxDy = Math.min(maxDy, bounds.height - shb.dims.height - shb.pos.y)
			}
			const clampedDx = Math.max(minDx, Math.min(dx, maxDx))
			const clampedDy = Math.max(minDy, Math.min(dy, maxDy))
			for (const id of dragIds) {
				const sb = startBboxes[id]  // raw pos.top for the position write
				if (!sb)
					continue;
				impls[id]?.writePos?.({ x: sb.pos.x + clampedDx, y: sb.pos.y + clampedDy })
			}
		},
		() => {
			const moves: MultiMoveEntry[] = []
			for (const id of dragIds) {
				const sb   = startBboxes[id]
				const bbox = impls[id]?.getBBox?.()
				if (!sb || !bbox)
					continue;
				if (bbox.pos.x !== sb.pos.x || bbox.pos.y !== sb.pos.y) {
					// Store design box positions (identity for all but hugging fields); see commitSingleMove.
					const after  = impls[id]!.storedPositionOf(bbox.pos)
					const before = impls[id]!.storedPositionOf(sb.pos)
					setObjectPositionSize(objCtx, id, after, undefined)
					moves.push({ id, before, after, detachedSlot: slots[id] ?? null })
				}
				// Detached but never actually moved (the whole selection stayed put): back onto the
				// template, so nothing is left off the layout without a history entry to save it.
				else if (slots[id])
					setDetachedInStore(objCtx, id, null)
			}
			if (moves.length > 0)
				histCtx.execute(moveMultipleObjectsCmd(objCtx, moves))
		},
	)
}

// Commit a single-object drag: persist the design box position (not the live hugged one, so a reload
// re-anchors to the same spot and re-exporting untouched fields never nudges them) and push the move
// to history. Shared by the own-<g> and intercept-rect drag paths; `label` only tags the debug log.
//
// slot is the template slot this gesture detached (null when it detached nothing — an unlinked object,
// or one already detached by an earlier drag). It rides on the command so undo relinks the leaf rather
// than leaving it off the layout at its old position. A press that never moved detaches nothing
// permanently: the leaf goes straight back under the template, so a bare click can't quietly stop it
// reflowing — and can't leave a detachment behind that no history entry would ever save.
function commitSingleMove<O extends DraggableImpl>(impl:O,startPos: Position,slot: Dimensions | null,objCtx:ObjectContext,histCtx:HistoryContext)
{
	const afterInk = { x: impl.box.pos.x, y: impl.box.pos.y }
	if (afterInk.x === startPos.x && afterInk.y === startPos.y) {
		if (slot) setDetachedInStore(objCtx, impl.id, null)
		return
	}
	const after  = impl.storedPositionOf(afterInk)
	const before = impl.storedPositionOf(startPos);
	setObjectPositionSize(objCtx, impl.id, after, undefined);
	histCtx.execute(moveObjectCmd(objCtx, impl.id, before, after, slot))
}


// ── Intercept-rect drag (handles-object rect on top of everything) ──────────
// A plain click here never moves the selection: this rect covers the selected object, so a click on
// it drags what is selected even where another object is painted over it, or where the object is a
// container its own children cover. Reaching one of those is Alt-click's job (cycleSelectionAt) —
// the selection-transferring click redirects this used to make (drill into the child under the
// cursor, hand the selection to whatever was topmost) fought every attempt to drag or resize an
// object that overlaps another.
// Double-click: forwarded to the impl's handleDoubleClick (via onDblClick on the rect below).
function onInterceptMouseDown<O extends DraggableImpl>(e: MouseEvent,parentId:string|null,impl:O,canvas:Canvas,selCtx:SelectionContext,impls:ImplRegistry,dragCtx:DragContext,objCtx:ObjectContext,histCtx:HistoryContext,design:DesignLinkContext,mode:Mode)
{
	e.stopPropagation();
	e.preventDefault();

	if (e.ctrlKey || e.metaKey) {
		selCtx.toggleSelect(impl.id);
		return;
	}

	// Alt-click: cycle the selection through everything under the cursor (see cycleSelectionAt) — the
	// only way a click moves the selection off the object this rect belongs to.
	if (e.altKey) {
		cycleSelectionAt({ x: e.clientX, y: e.clientY }, dragCtx.svgRef(), selCtx, impls);
		return;
	}

	// Shift-drag: an image's crop-pan when supplied, otherwise reserved (no canvas move).
	if (e.shiftKey) {
		impl.onShiftDrag(e, dragCtx, histCtx);
		return;
	}

	// A flex-laid-out child can't be freely positioned, but it can be dragged up/down to
	// reorder among its siblings. Once the child is selected its intercept rect covers it
	// (on top of everything), so the drag arrives here rather than on the child's own <g> —
	// start the reorder drag from here too, otherwise a selected child can't be reordered.
	if (impl.isLayoutManaged()) {
		startReorderDrag(e,parentId,canvas.height,impl,impls,dragCtx,objCtx,histCtx); 
		return; 
	}

	// Non-movable elements (a full-bleed image) keep their selection but never drag.
	if (!impl.isMovable())
		return;

	// A linked leaf detaches here exactly as it does on its own <g> (see detachIfLinked): this is the
	// path a drag takes once the element is selected and its intercept rect has appeared, which is the
	// common case for a user-mode drag — so leaving it out left those drags following the template.
	const detached = detachIfLinked(impl,design,objCtx,mode);

	// getSize() = the live hugged box for an un-resized user-mode field (pos otherwise), so the
	// canvas-edge clamp below matches the visible box rather than a stale stored width.
	const startBox: Box = { pos: detached?.pos ?? { x: impl.box.pos.x, y: impl.box.pos.y }, dims: impl.getSize() };
	const bounds: Dimensions = { width: canvas.width ?? Infinity, height: canvas.height ?? Infinity };
	const coy = impl.contentOffsetY();
	let moved = false;

	dragCtx.beginDrag({ x: e.clientX, y: e.clientY },
		(dx, dy) => {
			moved = true
			impl.writePos(clampDragPos(startBox, dx, dy, bounds, coy))
		},
		() => {
			// A press that never moved keeps the current selection — and gives back any detachment it
			// took, so merely clicking can't stop a leaf reflowing.
			if (!moved) {
				if (detached) setDetachedInStore(objCtx, impl.id, null)
				return
			}
			commitSingleMove(impl,startBox.pos,detached?.slot ?? null,objCtx,histCtx)
		},
	);
}


